import type {
  OuraDailyEntry,
  OuraReadiness,
  OuraActivity,
  OuraSleep,
  BackendSyncStatus,
} from "../data/ouraTypes";

import { core } from "@tauri-apps/api";

import { debugHealth } from "@/domains/logging/utils/debugLog";

/**
 * WHAT:
 *   Fetches the latest Oura entry from Supabase via backend.
 *
 * WHY:
 *   Provides access to most recent Oura daily entry for health analysis.
 *
 * @returns Promise resolving to OuraDailyEntry or null if no data found
 *
 */
export async function getLatestOuraEntry(): Promise<null | OuraDailyEntry> {
  try {
    const entry = await core.invoke<OuraDailyEntry>("get_latest_oura_data");
    return entry;
  } catch (error) {
    // NOTE: get_latest_oura_data failed - return null
    // Error is intentionally swallowed as null return is expected behavior
    debugHealth("debug", "ouraService", "getLatestOuraEntry failed", {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

/**
 * WHAT:
 *   Fetches the latest Oura entry directly from API (current day only).
 *
 * WHY:
 *   Provides real-time Oura data for current day without database lookup.
 *
 * @returns Promise resolving to OuraDailyEntry or null if no data found
 *
 */
export async function fetchLatestOuraEntry(): Promise<null | OuraDailyEntry> {
  try {
    const entry = await core.invoke<OuraDailyEntry>("fetch_oura_latest");
    return entry;
  } catch (error) {
    // NOTE: fetch_oura_latest fehlgeschlagen
    // Error is intentionally swallowed as null return is expected behavior
    debugHealth("debug", "ouraService", "fetchLatestOuraEntry failed", {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

/**
 * WHAT:
 *   Aggregates Readiness, Activity, Sleep into a daily entry dataset.
 *
 * WHY:
 *   Combines multiple Oura data sources into a single daily entry for analysis.
 *
 * @param readiness - Oura readiness data
 *
 * @param activity - Oura activity data
 *
 * @param sleep - Oura sleep data
 *
 * @returns Promise resolving to OuraDailyEntry
 *
 */
export async function fetchOuraDailyEntry(
  readiness: null | OuraReadiness,
  activity: null | OuraActivity,
  sleep: null | OuraSleep,
): Promise<OuraDailyEntry> {
  try {
    const entry = await core.invoke<OuraDailyEntry>("fetch_oura_daily_entry", {
      activity,
      readiness,
      sleep,
    });
    return entry;
  } catch (error) {
    // NOTE: fetch_oura_daily_entry fehlgeschlagen
    debugHealth("error", "ouraService", "fetchOuraDailyEntry failed", {
      error: error instanceof Error ? error.message : String(error),
    });
    throw new Error("Tagesdaten konnten nicht aggregiert werden.");
  }
}

/**
 * WHAT:
 *   Synchronizes current Oura data from API and saves it.
 *
 * WHY:
 *   Provides sync functionality to fetch and store latest Oura health data.
 *
 * @returns Promise resolving to BackendSyncStatus
 *
 */
export async function syncOuraDataFromAPI(): Promise<BackendSyncStatus> {
  try {
    const rawResult = await core.invoke<{
      status: string;
      data?: OuraDailyEntry;
    }>("sync_oura_data");

    // eslint-disable-next-line eqeqeq -- Reason: Need to check both null and undefined for object type narrowing
    if (typeof rawResult === "object" && rawResult != null && "status" in rawResult) {
      const status = rawResult.status;
      if (
        status === "NewEntrySaved" &&
        "data" in rawResult &&
        typeof rawResult.data === "object" &&
        // eslint-disable-next-line eqeqeq -- Reason: Need to check both null and undefined for object type narrowing
        rawResult.data != null
      ) {
        return { data: rawResult.data, status: "NewEntrySaved" };
      }
      if (status === "AlreadySynced") {
        return { status: "AlreadySynced" };
      }
      if (status === "NoDataAvailable") {
        return { status: "NoDataAvailable" };
      }
    }
    // Unbekannter Sync-Status
    return { status: "NoDataAvailable" }; // optionaler Fallback
  } catch (error) {
    // NOTE: sync_oura_data fehlgeschlagen
    debugHealth("error", "ouraService", "syncOuraDataFromAPI failed", {
      error: error instanceof Error ? error.message : String(error),
    });
    throw new Error("Synchronisierung mit Oura fehlgeschlagen.");
  }
}
